import { TableColumnType } from 'antd';
import { isNaN, round } from 'lodash';
import { useMemo } from 'react';
import {
  TemplateInsightGroupBy,
  TemplateInsightResponses,
} from 'api/template-insight';
import { formatter } from 'components/atoms/input-duration';
import { useTranslate } from 'hooks/use-translation';
import {
  useTimeTrackedTaskColumn,
  useTimeTrackedTemplateColumn,
} from './columns';

const getCategoryTitle = <
  T extends
    | TemplateInsightGroupBy.ProjectTimeTracked
    | TemplateInsightGroupBy.TaskTimeTracked,
>(
  templateInsight: TemplateInsightResponses<T>,
  noCategoryLabel: string,
) => {
  const columnTitle: TableColumnType<any>[] = [];
  const responses = templateInsight?.templateResponses ?? [];

  responses.forEach((response) => {
    response.categories?.forEach((category) => {
      if (columnTitle.find(({ key }) => key === category.categoryId)) {
        return;
      }
      columnTitle.push({
        title: category.categoryName || noCategoryLabel,
        dataIndex: category.categoryId,
        key: category.categoryId,
      });
    });
  });

  return columnTitle;
};

export const useTimeTrackedTemplateTable = ({
  templateInsight,
}: {
  templateInsight?: TemplateInsightResponses<TemplateInsightGroupBy.ProjectTimeTracked>;
}) => {
  const translate = useTranslate();
  const commonColumn = useTimeTrackedTemplateColumn();

  return useMemo(() => {
    if (!templateInsight) {
      return {
        dataSource: undefined,
        tableColumn: undefined,
      };
    }
    const noCategoryLabel = translate('timesheets.column.no_category');
    const dataSource = templateInsight?.templateResponses.map(
      (templateMeta) => {
        const {
          template,
          categories,
          averageMinutesTracked,
          averageNonBillableMinutes,
          averageNonBillableMinutesPercentage,
          averageBillableMinutesPercentage,
          averageBillableMinutes,
          plannedEffortPercentage,
          plannedEffort,
          excessPlanned: excessHoursPlanned,
        } = templateMeta;
        const categoryData = categories?.reduce(
          (acc, category) => ({
            ...acc,
            [category.categoryId]: `${round(
              category.averageMinutesTrackedPercentage,
            )}% (${formatter(category.averageMinutesTracked)})`,
          }),
          {},
        );

        return {
          template,
          templateName: template.templateName,
          plannedEffort,
          avgHoursTracked: averageMinutesTracked,
          plannedEffortPercentage: !isNaN(round(plannedEffortPercentage))
            ? round(plannedEffortPercentage)
            : null,
          excessHoursPlanned,
          averageNonBillingTracked: {
            value:
              averageNonBillableMinutes !== null
                ? averageNonBillableMinutes
                : 0,
            percentage: averageNonBillableMinutesPercentage
              ? round(averageNonBillableMinutesPercentage)
              : 0,
          },
          averageBillingTracked: {
            value: averageBillableMinutes,
            percentage:
              averageBillableMinutesPercentage != null
                ? round(averageBillableMinutesPercentage)
                : 0,
          },
          ...categoryData,
        };
      },
    );

    const categoriesTitle = getCategoryTitle(templateInsight, noCategoryLabel);
    const tableColumn = [...commonColumn, ...categoriesTitle];

    return { dataSource, tableColumn };
  }, [templateInsight, translate, commonColumn]);
};

export const useTimeTrackedTaskTable = ({
  templateInsight,
}: {
  templateInsight?: TemplateInsightResponses<TemplateInsightGroupBy.TaskTimeTracked>;
}) => {
  const translate = useTranslate();

  const dataSource = useMemo(
    () =>
      templateInsight?.templateResponses.map((templateMeta) => {
        const {
          taskTemplate: { sourceName: taskName, sourceId: taskId },
          template,
          categories,
          averageMinutesTracked,
          averageNonBillableMinutes,
          averageNonBillableMinutesPercentage,
          averageBillableMinutesPercentage,
          averageBillableMinutes,
          plannedEffortPercentage,
          plannedEffort,
          excessPlanned: excessHoursPlanned,
          phaseTemplate: { sourceName: phase },
        } = templateMeta;
        const categoryData = categories?.reduce(
          (acc, category) => ({
            ...acc,
            [category.categoryId]: `${round(
              category.averageMinutesTrackedPercentage,
            )}% (${formatter(category.averageMinutesTracked)})`,
          }),
          {},
        );

        return {
          taskId,
          taskName,
          template,
          templateName: template.templateName,
          phase,
          plannedEffort,
          avgHoursTracked: averageMinutesTracked,
          plannedEffortPercentage: !isNaN(round(plannedEffortPercentage))
            ? round(plannedEffortPercentage)
            : null,
          excessHoursPlanned,
          averageNonBillingTracked: {
            value:
              averageNonBillableMinutes !== null
                ? averageNonBillableMinutes
                : 0,
            percentage: averageNonBillableMinutesPercentage
              ? round(averageNonBillableMinutesPercentage)
              : 0,
          },
          averageBillingTracked: {
            value: averageBillableMinutes,
            percentage:
              averageBillableMinutesPercentage != null
                ? round(averageBillableMinutesPercentage)
                : 0,
          },
          ...categoryData,
        };
      }),
    [templateInsight?.templateResponses],
  );

  const commonColumn = useTimeTrackedTaskColumn();

  const tableColumn = useMemo(() => {
    const noCategoryLabel = translate('timesheets.column.no_category');

    const categories = templateInsight
      ? getCategoryTitle(templateInsight, noCategoryLabel)
      : [];

    return [...commonColumn, ...categories];
  }, [templateInsight, translate, commonColumn]);

  return { dataSource, tableColumn };
};
